import { useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotificationPanelStore } from '@/notifications/store/notification-panel-store';
import { formatNotificationDate } from '@/notifications/lib/relative-time';
import type { NotificationModel } from '@/shared/types/notifications';
import { NotificationHtmlMessage } from './notification-html-message';

/**
 * Per-row layout — Flutter parity with
 * `notification_center_row_widget.dart:39-107`.
 *
 * - Avatar slot uses the bell icon (Flutter falls back to `ic_appicon`
 *   when no per-notification image exists, which is the case for every
 *   server-issued notification today).
 * - Sanitized HTML message via `<NotificationHtmlMessage>`
 * - Relative-time stamp via `formatNotificationDate` (Flutter-verbatim
 *   breakpoints — see `relative-time.ts`).
 * - **Unread tinted bg** when `!isRead` — uses the Signal /100 ramp on
 *   the brand-blue family per CLAUDE.md tinted-pill rule (bg → /100,
 *   text → /700 reads against the /100). Read rows are white.
 * - On visibility >50% (IntersectionObserver default threshold trip),
 *   the row reports up two signals:
 *     1. `onVisibleNearEnd(index)` for infinite-scroll prefetch — the
 *        panel decides whether to call `fetchNextPage` (Flutter's
 *        `index > total - 5` guard at row_widget context).
 *     2. `markSeenWhileOpen(id)` if the row is unread — drained on
 *        panel close to fire MarkNotificationAsRead.
 *
 * **Threshold note**: Flutter uses `visiblePercentage > 80.00`
 * (visibility_detector lib). React uses `IntersectionObserver` with
 * default `threshold: 0.5` (50%). The two are functionally equivalent
 * for the panel's purpose (a row is either scrolled into view or it
 * isn't); the exact percentage isn't load-bearing.
 */
interface Props {
  notification: NotificationModel;
  index: number;
  totalCount: number;
  onVisibleNearEnd: () => void;
  onClick: () => void;
}

export function NotificationRow({
  notification,
  index,
  totalCount,
  onVisibleNearEnd,
  onClick,
}: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const markSeenWhileOpen = useNotificationPanelStore((s) => s.markSeenWhileOpen);
  const onVisibleNearEndRef = useRef(onVisibleNearEnd);
  onVisibleNearEndRef.current = onVisibleNearEnd;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Flutter's "index > total - 5" guard — within the last 5 rows of
    // the currently-known list, request the next page.
    const nearEnd = index > totalCount - 5;
    const isUnread = !notification.isRead;
    if (!nearEnd && !isUnread) return; // nothing to observe for

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (nearEnd) onVisibleNearEndRef.current();
          if (isUnread) markSeenWhileOpen(notification.id);
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [
    index,
    totalCount,
    notification.id,
    notification.isRead,
    markSeenWhileOpen,
  ]);

  // PROD-1015 [INTENTIONAL_DIVERGENCE: null-guard] — Flutter parses
  // `createdOn` via `DateTime.parse` with the value assumed non-null and
  // crashes when the field is absent (notification_model.dart:29,41,54).
  // React null-guards instead: the shared wire type declares
  // `createdOn: string`, but the field is `DateTime?` in Flutter and can be
  // missing on the wire. Guard absent/invalid values and hide the timestamp
  // line rather than throw. The present-case formatting is unchanged.
  const createdOn = notification.createdOn
    ? new Date(notification.createdOn)
    : null;
  const hasValidCreatedOn = createdOn !== null && !Number.isNaN(createdOn.getTime());

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-[var(--notif-row-radius)] p-3 transition-colors',
        notification.isRead
          ? 'bg-[var(--surface-read)] hover:bg-grey-100'
          : 'bg-[var(--surface-new)] hover:bg-[var(--accent-active-bg)]',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-[var(--notif-icon-size)] shrink-0 items-center justify-center rounded-full bg-[var(--color-grey-100)] text-[var(--ink-secondary)]">
          <Bell className="h-5 w-5 text-[var(--accent-primary)]" />
        </div>
        <div className="min-w-0 flex-1">
          <NotificationHtmlMessage html={notification.message} />
          {hasValidCreatedOn && (
            <div className="mt-1 [font:var(--type-meta)] text-[var(--ink-secondary)]">
              {formatNotificationDate(createdOn)}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
